import { formatDate } from '@fullcalendar/core';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useRedirect } from 'react-admin';
import { useLocation } from 'react-router-dom';
import {
  AlertType,
  DetectedDocumentResponse,
  DocumentDetailResponse,
  GenderType,
  PatientDocumentResponse,
  PhoneNumberType,
  ReferralDocumentResponse,
} from '../../../../interfaces/document';
import {
  ReferralBmiSource,
  ReferralDemographics,
  ReferralInsurance,
  TelephoneEntry,
} from '../../../../interfaces/referral';
import referralService from '../../../../services/referral';
import { getLeadDetailsUrl } from '../../../../util/url';
import { LeadSystemSource } from '../../../../interfaces/onboarding';
import { cohortLabel, PROGRAM_COHORT_OPTIONS } from '../../../../lib/referrals/cohorts';
import { Clinic, getClinicDisplayName } from '../../../../utils/clinic.utils';
import { formatGenderDisplay, getPhoneNumberByType } from '../../../../utils/patient.utils';
import { PageHeader } from '../../../common/PageHeader';
import { getStatusColorStyles } from '../../shared';
import { buildEditableInsurance, nextCoverageType } from '../buildEditableInsurance';
import { buildReferralUpdateRequest, primaryCoverageProblem } from '../buildReferralUpdateRequest';
import {
  applyMeasurementEdit,
  BMI_RANGE_TEXT,
  bmiFromMeasurements,
  bmiReviewReason,
  bmiSourceNote,
  calculatedBmiOutOfRange,
  emptyBmiHint,
  followCalculatedBmi,
  isBmiDerivedOnLoad,
  isEstimatedBmiSource,
  measurementError,
  MeasurementKey,
  measurementProblem,
} from '../referralMeasurements';
import CoverageDetail from '../CoverageDetail';
import FieldWrapper from '../FieldWrapper';
import DiscardReferralDialog from '../DiscardReferralDialog';
import PropagationStatus from '../PropagationStatus';
import EligibilityTaskLink from '../../../referrals/EligibilityTaskLink';
import { getDocumentsLibraryUrlWithFilters } from '../../utils/navigationHelpers';
import { BMI_RANGE } from '../../../../util/validations';

interface ReferralDocumentsTabProps {
  document: DocumentDetailResponse;
  clinics?: Clinic[];
  onReferralUpdate?: (referralId: number, success: boolean, message: string) => void;
  /**
   * Show only these referrals. The Referrals section opens one referral next to its document
   * and reuses this card — its edit, save and discard — rather than a second copy of them.
   */
  referralIds?: number[];
  /** The document page's header and title. Off where the card is embedded in another page. */
  showHeader?: boolean;
}

interface EditReferralData {
  businessClinicId: number | null;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  email: string;
  mobilePhone: string;
  homePhone: string;
  otherPhone: string;
  bmi: string;
  heightFeet: string;
  heightInch: string;
  weightLb: string;
  bmiDerived: boolean;
  bmiFallback?: string;
  /**
   * How leads-api says the loaded BMI was obtained, as loaded. Absent on an API before 0.64.0,
   * which stores a cleared BMI as sent; 0.64.0 derives it again (`emptyBmiHint`).
   */
  bmiSource?: ReferralBmiSource | null;
  insurance: ReferralInsurance[];
  isCommunityReferral: boolean;
  systemSource: string;
  referringProvider: string;
  communityReferralClinicName: string;
}

const MEASUREMENT_FIELDS: ReadonlyArray<{ key: MeasurementKey; label: string; step: string }> = [
  { key: 'heightFeet', label: 'Height (ft)', step: '1' },
  { key: 'heightInch', label: 'Height (in)', step: '1' },
  { key: 'weightLb', label: 'Weight (lb)', step: '0.1' },
];

/**
 * What the BMI field says under the input: why its value cannot be saved; that it is derived
 * from the measurements; or what they give when the field holds something else, so a
 * contradiction is visible before it is saved. `offerCalculated` shows "Use calculated BMI",
 * the only way back to following the measurements: an emptied field stays empty.
 */
const bmiFieldState = (
  editData: EditReferralData,
): { error: string | null; helperText?: string; offerCalculated: boolean } => {
  const error = measurementError('bmi', editData.bmi ?? '');
  const derived = bmiFromMeasurements(editData);
  const outOfRange = calculatedBmiOutOfRange(editData);
  // Never offered as a value: the measurements are what to check.
  const outOfRangeText =
    outOfRange !== null
      ? `Height and weight give ${outOfRange}, outside ${BMI_RANGE_TEXT}; check them.`
      : undefined;
  const typed = (editData.bmi ?? '').trim();
  if (editData.bmiDerived) {
    // An estimate stands while height and weight give no BMI, and is still one.
    const estimatedFrom =
      editData.bmiSource === 'icd10' ? 'Estimated from ICD-10 code' : 'Estimated from the document';
    const estimate =
      derived === null && typed !== '' && typed === editData.bmiFallback
        ? `${estimatedFrom}; height and weight replace it`
        : undefined;
    return {
      error,
      helperText:
        error ?? outOfRangeText ?? (derived !== null ? 'Derived from height and weight' : estimate),
      offerCalculated: false,
    };
  }
  const offerCalculated = derived !== null && (typed === '' || derived !== Number(typed));
  // leads-api 0.64.0 derives a cleared BMI again on save, so say what it will store rather than
  // let an empty field read as "no BMI will be stored".
  const rederived = typed === '' ? emptyBmiHint(editData.bmiSource, derived) : null;
  const calculated = offerCalculated ? `Height and weight give ${derived}.` : null;
  return {
    error,
    helperText:
      error ?? outOfRangeText ?? ([calculated, rederived].filter(Boolean).join(' ') || undefined),
    offerCalculated,
  };
};

/** How read mode marks a BMI leads-api derived; nothing for a stated BMI or none recorded. */
const bmiReadNote = (bmiSource: ReferralDocumentResponse['bmiSource']) => {
  const note = bmiSourceNote(bmiSource);
  return note ? (
    <Typography
      variant='caption'
      display='block'
      sx={{ mt: 0.5 }}
      color={note.needsReview ? 'warning.dark' : 'text.secondary'}>
      {note.text}
    </Typography>
  ) : null;
};

const storedOrExtracted = (stored: number | null | undefined, extracted: string | undefined) =>
  stored === undefined ? String(extracted ?? '') : String(stored ?? '');

interface CombinedReferralData {
  referralDocument: ReferralDocumentResponse;
  patientDocument: PatientDocumentResponse;
  detectedDocument: DetectedDocumentResponse;
  extractedData: Record<string, string>;
  fieldConfidence: Record<string, number>;
}


export const ReferralDocumentsTab: React.FC<ReferralDocumentsTabProps> = ({
  document,
  clinics = [],
  onReferralUpdate,
  referralIds,
  showHeader = true,
}) => {
  const redirect = useRedirect();
  const location = useLocation();
  const [editing, setEditing] = useState<string | null>(null);
  const [editData, setEditData] = useState<EditReferralData>({} as EditReferralData);
  const [saving, setSaving] = useState(false);
  const [discarding, setDiscarding] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // `referralId` puts the message inside that referral's card: the tab-level alert can be
  // off-screen on the second or third card, which made a refused Save look like it did nothing.
  const [updateMessage, setUpdateMessage] = useState<{
    type: AlertType;
    text: string;
    referralId?: number;
  } | null>(null);
  // The coverage rows the open edit started from, whether stored or pre-filled from the
  // document's extraction. Lets the save leave `insurance` out when no coverage was touched.
  const [loadedInsurance, setLoadedInsurance] = useState<ReferralInsurance[] | null>(null);
  // The demographics the open edit started from, so a blanked measurement clears a stored one.
  const [storedDemographics, setStoredDemographics] = useState<ReferralDemographics | null>(null);
  // Referrals discarded from this tab, by id, until the refresh, which brings back
  // `discardedAt` (leads-api #125). Without it the card stayed editable for the two seconds
  // before the refresh, and an edit
  // opened in that window was thrown away when it landed, or a second Discard answered 409.
  const [locallyDiscarded, setLocallyDiscarded] = useState<Record<number, string>>({});
  // Set by a successful save until the parent hands down the refreshed `document`. Until then
  // `document` still holds the values from before the save, and the save sends the whole
  // demographics block, so an Edit opened in that window would send them back and silently
  // revert the save in LeadSquared, Eligibility and Core. Edit stays off rather than resetting
  // an open edit when the refresh lands: a reset would still lose to a Save clicked before it.
  // This is component state, so it only holds if the parent keeps the tab mounted through its
  // refresh and keeps the old `document` when the refresh fails: `DocumentDetails` does both
  // (no skeleton once a document is loaded) and offers a retry. A parent that remounts the tab
  // on refresh would reset it.
  const [awaitingRefresh, setAwaitingRefresh] = useState(false);
  useEffect(() => {
    setAwaitingRefresh(false);
  }, [document]);

  // The deferred refreshes below outlive a navigation away; clear them so they do not update
  // a tab that is gone or refresh a document the operator already left. Clearing at unmount is
  // not enough on its own: a save still in flight then resolves after the cleanup ran and would
  // schedule a fresh timer. `live` gates that continuation, as it does in DiscardReferralDialog.
  const live = useRef(true);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const later = (callback: () => void, ms: number) => {
    if (!live.current) {
      return;
    }
    timers.current.push(setTimeout(callback, ms));
  };
  useEffect(() => {
    live.current = true;
    // The array is mutated in place, never replaced, so this reference sees every timer.
    const pending = timers.current;
    return () => {
      live.current = false;
      pending.forEach(clearTimeout);
    };
  }, []);

  const getCombinedReferralData = (): CombinedReferralData[] => {
    return (
      document.referralDocuments?.map((referralDoc) => {
        let patientDocument = null;
        let detectedDocument = null;
        let extractedData = null;
        let fieldConfidence = null;

        for (const dd of document.detectedDocuments || []) {
          for (const pd of dd.patientDocuments || []) {
            if (pd.referralDocument?.id === referralDoc.id) {
              patientDocument = pd;
              detectedDocument = dd;
              extractedData = pd.extractedData || {};
              fieldConfidence = extractedData.field_confidence || {};
              break;
            }
          }
          if (patientDocument) {
            break;
          }
        }

        return {
          referralDocument: referralDoc as ReferralDocumentResponse,
          patientDocument: patientDocument as PatientDocumentResponse,
          detectedDocument: detectedDocument as DetectedDocumentResponse,
          extractedData: extractedData || {},
          fieldConfidence: fieldConfidence || {},
        };
      }) || []
    );
  };

  const handleEdit = (
    referralDoc: ReferralDocumentResponse,
    _detectedDoc: DetectedDocumentResponse,
    extractedData: Record<string, string>,
  ) => {
    setEditing(referralDoc.id.toString());

    const demographics = referralDoc.demographics || ({} as ReferralDemographics);
    const telephone = demographics.telephone || [];

    const insuranceArray = buildEditableInsurance(referralDoc.insurance, extractedData);
    setLoadedInsurance(insuranceArray.map((coverage) => ({ ...coverage })));

    const loaded = {
      businessClinicId: referralDoc.clinicId ?? null,
      firstName: demographics.firstName || extractedData.patient_first_name || '',
      lastName: demographics.lastName || extractedData.patient_last_name || '',
      dateOfBirth: demographics.dateOfBirth || extractedData.patient_date_of_birth || '',
      gender: demographics.gender || extractedData.patient_gender || '',
      street: demographics.street || extractedData.patient_street || '',
      city: demographics.city || extractedData.patient_city || '',
      state: demographics.state || extractedData.patient_state || '',
      zipCode: demographics.zipCode || extractedData.patient_zip_code || '',
      email: (demographics.email || extractedData.patient_email || '').toLowerCase(),
      mobilePhone:
        telephone.find((t: TelephoneEntry) => t.type === PhoneNumberType.Cell)?.number ||
        extractedData.patient_phone_number_mobile ||
        '',
      homePhone:
        telephone.find((t: TelephoneEntry) => t.type === PhoneNumberType.Home)?.number ||
        extractedData.patient_phone_number_home ||
        '',
      otherPhone:
        telephone.find((t: TelephoneEntry) => t.type === PhoneNumberType.Other)?.number ||
        extractedData.patient_phone_number_other ||
        '',
      bmi: demographics.bmi != null ? String(demographics.bmi) : '',
      // Stored values first, then what the document stated, as for the other fields — but
      // only when nothing was ever stored. A stored `null` is a measurement someone cleared,
      // and falling back to the extraction would bring the misread value straight back.
      heightFeet: storedOrExtracted(demographics.heightFeet, extractedData.patient_height_feet),
      heightInch: storedOrExtracted(demographics.heightInch, extractedData.patient_height_in),
      weightLb: storedOrExtracted(demographics.weightLb, extractedData.patient_weight_lb),
      insurance: insuranceArray,
      isCommunityReferral: referralDoc.isCommunityReferral || false,
      // The stored cohort, not one derived from `isCommunityReferral`. That boolean cannot
      // represent a third value, so deriving it showed a Maintain referral as a plain
      // partner one — and the edit would then have silently corrected it to that.
      systemSource:
        referralDoc.systemSource ||
        (referralDoc.isCommunityReferral
          ? LeadSystemSource.CommunityReferral
          : LeadSystemSource.PartnerReferral),
      referringProvider: referralDoc.referralSource?.provider?.fullName || '',
      communityReferralClinicName: referralDoc.referralSource?.sourceClinicName || '',
    };
    // A BMI that follows the measurements starts from them, as the manual form's does. How
    // leads-api says it was obtained (`bmiSource`, 0.64.0) decides whether it follows; without
    // it, the stored values do.
    const bmiDerived = isBmiDerivedOnLoad(loaded, demographics.bmi, referralDoc.bmiSource);
    const derivedBmi = bmiDerived ? bmiFromMeasurements(loaded) : null;
    setEditData({
      ...loaded,
      bmiDerived,
      bmiFallback:
        bmiDerived && isEstimatedBmiSource(referralDoc.bmiSource) ? loaded.bmi : undefined,
      bmiSource: referralDoc.bmiSource,
      bmi: derivedBmi !== null ? String(derivedBmi) : loaded.bmi,
    });
    setStoredDemographics(referralDoc.demographics ?? null);
  };

  const handleSave = async () => {
    if (!editing) {
      return;
    }

    setSaving(true);
    setUpdateMessage(null);

    const referralId = parseInt(editing, 10);

    try {
      const invalidMeasurement = measurementProblem(editData);
      if (invalidMeasurement) {
        setUpdateMessage({ type: AlertType.Error, text: invalidMeasurement, referralId });
        return;
      }

      const updateRequest = buildReferralUpdateRequest(
        editData,
        loadedInsurance,
        storedDemographics,
      );

      const problem = primaryCoverageProblem(updateRequest);
      if (problem) {
        setUpdateMessage({ type: AlertType.Error, text: problem, referralId });
        return;
      }

      const response = await referralService.updateReferralDocument(referralId, updateRequest);
      if (!live.current) {
        return;
      }

      if (response?.success) {
        setUpdateMessage({
          type: AlertType.Success,
          text: response.message || 'Referral updated successfully',
          referralId,
        });
        setEditing(null);
        setEditData({} as EditReferralData);
        setLoadedInsurance(null);
        if (onReferralUpdate) {
          setAwaitingRefresh(true);
        }

        later(() => {
          // Only this card's message: a refusal another card got in the meantime stays up.
          setUpdateMessage((current) => (current?.referralId === referralId ? null : current));
          if (onReferralUpdate) {
            setRefreshing(true);
            onReferralUpdate(referralId, true, response.message);
            later(() => setRefreshing(false), 2000);
          }
        }, 2000);
      } else {
        throw new Error(response?.message || 'Failed to update referral');
      }
    } catch (error) {
      if (!live.current) {
        return;
      }
      // A fixed string: the error can carry the API's validation message, which the card shows
      // and the console does not need.
      console.error('Saving the referral failed');
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setUpdateMessage({
        type: AlertType.Error,
        text: errorMessage,
        referralId,
      });
    } finally {
      if (live.current) {
        setSaving(false);
      }
    }
  };

  const handleCancel = () => {
    setEditing(null);
    setEditData({} as EditReferralData);
    setLoadedInsurance(null);
    setUpdateMessage(null);
  };

  const addInsuranceRow = () => {
    // A member can carry a third policy. The API accepted only a pair until leads-api 0.61.0,
    // so this capped at two and a tertiary coverage could not be recorded at all.
    const nextType = nextCoverageType(editData.insurance);
    if (!nextType) {
      return;
    }

    setEditData((prev) => ({
      ...prev,
      insurance: [
        ...prev.insurance,
        {
          type: nextType,
          carrier: '',
          policyNumber: '',
          groupNumber: '',
          policyHolder: '',
        },
      ],
    }));
  };

  const removeInsuranceRow = (index: number) => {
    setEditData((prev) => ({
      ...prev,
      insurance: prev.insurance.filter((_, i) => i !== index),
    }));
  };

  const updateInsurance = (index: number, field: keyof ReferralInsurance, value: string) => {
    setEditData((prev) => ({
      ...prev,
      insurance: prev.insurance.map((ins, i) => {
        if (i === index) {
          return { ...ins, [field]: value };
        }
        return ins;
      }),
    }));
  };

  const combinedReferrals = getCombinedReferralData().filter(
    ({ referralDocument }) => !referralIds || referralIds.includes(referralDocument.id),
  );

  if (combinedReferrals.length === 0) {
    return (
      <Alert severity='info'>
        No referral documents found. Referrals are created when the system detects referral-type
        documents with patient information.
      </Alert>
    );
  }

  return (
    <Box>
      {showHeader && (
        <PageHeader
          showBackButton={true}
          onBack={() => redirect(getDocumentsLibraryUrlWithFilters(location.search))}
          backButtonLabel='Back to Library'
          breadcrumbs={[
            {
              label: 'Documents',
              onClick: () => redirect(getDocumentsLibraryUrlWithFilters(location.search)),
            },
            {
              label: 'Library',
              onClick: () => redirect(getDocumentsLibraryUrlWithFilters(location.search)),
            },
            { label: `${document.externalId || document.documentId}`, active: true },
          ]}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: showHeader ? 3 : 1 }}>
        {showHeader && (
          <Typography variant='h6'>Referral Documents ({combinedReferrals.length})</Typography>
        )}
        {refreshing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant='caption' color='text.secondary'>
              Refreshing data...
            </Typography>
          </Box>
        )}
      </Box>

      {updateMessage && updateMessage.referralId === undefined && (
        <Alert severity={updateMessage.type} sx={{ mb: 3 }} onClose={() => setUpdateMessage(null)}>
          {updateMessage.text}
        </Alert>
      )}

      {combinedReferrals.map(
        ({ referralDocument, patientDocument, detectedDocument, extractedData }) => {
          const isEditing = editing === referralDocument.id.toString();
          const discardedAt = referralDocument.discardedAt ?? locallyDiscarded[referralDocument.id];
          const liveMeasurementProblem = isEditing ? measurementProblem(editData) : null;
          const bmiField = bmiFieldState(editData);

          return (
            <Card key={referralDocument.id} variant='outlined' sx={{ mb: 3 }}>
              <CardHeader
                avatar={<PersonIcon color='primary' />}
                title={
                  <Typography variant='h6'>
                    {referralDocument.demographics?.fullName ||
                      patientDocument?.patientIdentifier ||
                      'Unknown Patient'}
                  </Typography>
                }
                subheader={
                  <Box>
                    {detectedDocument && (
                      <Typography variant='body2' color='text.secondary'>
                        Document: {detectedDocument.documentType} • Pages{' '}
                        {detectedDocument.pageStart}-{detectedDocument.pageEnd} •
                        {((detectedDocument.confidence || 0) * 100).toFixed(1)}% confidence
                      </Typography>
                    )}
                    {patientDocument?.crmMetadata?.id && (
                      <Link
                        component='button'
                        variant='caption'
                        onClick={() =>
                          window.open(getLeadDetailsUrl(patientDocument.crmMetadata.id), '_blank')
                        }
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          textDecoration: 'none',
                          mt: 0.5,
                        }}>
                        CRM: {patientDocument.crmMetadata.id}
                        <OpenInNewIcon sx={{ fontSize: 12 }} />
                      </Link>
                    )}
                    <Box sx={{ mt: 0.5 }}>
                      <EligibilityTaskLink
                        crmId={patientDocument?.crmMetadata?.id}
                        eligibilityPropagation={
                          referralDocument.syncState?.propagation?.eligibility
                        }
                      />
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      <PropagationStatus
                        propagation={referralDocument.syncState?.propagation}
                        discarded={Boolean(discardedAt)}
                      />
                    </Box>
                    {discardedAt && (
                      <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
                        Discarded on {new Date(discardedAt).toLocaleString()}
                        {referralDocument.discardReason ? `: ${referralDocument.discardReason}` : ''}
                      </Typography>
                    )}
                  </Box>
                }
                action={
                  <Box>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size='small'
                          variant='contained'
                          startIcon={
                            saving ? <CircularProgress size={16} color='inherit' /> : <SaveIcon />
                          }
                          onClick={handleSave}
                          disabled={saving}>
                          {saving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          size='small'
                          variant='outlined'
                          onClick={handleCancel}
                          disabled={saving}>
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      discardedAt ? (
                        // A discarded referral is read-only. Editing it would still propagate
                        // corrections to the CRM for a patient the team has decided to drop,
                        // and offering Discard again only earns a 409.
                        <Chip
                          size='small'
                          color='default'
                          variant='outlined'
                          icon={<DeleteIcon />}
                          label={`Discarded ${new Date(discardedAt).toLocaleDateString()}`}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip
                            title={
                              awaitingRefresh ? 'Loading the saved values before the next edit' : ''
                            }>
                            {/* A disabled button fires no events, so the Tooltip needs a span. */}
                            <span>
                              <Button
                                size='small'
                                variant='contained'
                                startIcon={<EditIcon />}
                                // Not while another card is open or saving: opening this one
                                // would drop that card's unsaved edit, and the refresh after a
                                // save would drop this one.
                                disabled={awaitingRefresh || editing !== null || saving}
                                onClick={() =>
                                  handleEdit(referralDocument, detectedDocument, extractedData)
                                }>
                                Edit
                              </Button>
                            </span>
                          </Tooltip>
                          <Button
                            size='small'
                            variant='outlined'
                            color='error'
                            startIcon={<DeleteIcon />}
                            // Not while another card is mid-edit: the refresh that follows a
                            // discard would throw that unsaved edit away.
                            disabled={editing !== null}
                            onClick={() => setDiscarding(referralDocument.id)}>
                            Discard
                          </Button>
                        </Box>
                      )
                    )}
                  </Box>
                }
              />

              {/* Status Information Banner - Always visible */}
              <Box
                sx={{
                  mx: 2,
                  mt: 1,
                  mb: 2,
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant='body2' color='text.secondary' fontWeight='500'>
                        Status:
                      </Typography>
                      <Chip
                        label={referralDocument.status}
                        size='small'
                        variant='outlined'
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          ...getStatusColorStyles(referralDocument.status),
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant='body2' color='text.secondary' fontWeight='500'>
                        Eligibility:
                      </Typography>
                      <Chip
                        label={referralDocument.eligibilityStatus}
                        size='small'
                        variant='outlined'
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          ...getStatusColorStyles(referralDocument.eligibilityStatus),
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant='body2' color='text.secondary' fontWeight='500'>
                        User ID:
                      </Typography>
                      <Typography variant='body2' color='text.primary'>
                        {referralDocument.userId || 'Not linked'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant='body2' color='text.secondary' fontWeight='500'>
                      Created:
                    </Typography>
                    <Typography variant='body2' color='text.primary'>
                      {formatDate(referralDocument.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <CardContent sx={{ p: 3 }}>
                {updateMessage?.referralId === referralDocument.id && (
                  <Alert
                    severity={updateMessage.type}
                    sx={{ mb: 3 }}
                    onClose={() => setUpdateMessage(null)}>
                    {updateMessage.text}
                  </Alert>
                )}
                {/* leads-api 0.64.0 flags a referral whose document gave neither a usable BMI nor
                    a complete height and weight, or whose BMI is unusual. Gone once a save fixes
                    it and the card reloads. Hidden while editing, where the BMI field says what
                    the save will store. */}
                {referralDocument.bmiNeedsReview === true && !discardedAt && !isEditing && (
                  <Alert severity='warning' sx={{ mb: 3 }}>
                    {bmiReviewReason(
                      referralDocument.bmiSource,
                      referralDocument.demographics?.bmi,
                    )}
                  </Alert>
                )}
                {/* Shown as soon as the edit opens, so a pre-filled value out of range is not
                    first reported by a refused Save. Hidden while Save reports the same text. */}
                {liveMeasurementProblem && updateMessage?.text !== liveMeasurementProblem && (
                  <Alert severity='warning' sx={{ mb: 3 }}>
                    {liveMeasurementProblem}
                  </Alert>
                )}

                {/* Clinic Assignment */}
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 3,
                      pb: 1,
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}>
                    <BusinessIcon sx={{ mr: 1 }} color='primary' />
                    <Typography variant='h6'>Clinic Assignment</Typography>
                    {isEditing && (
                      <Chip
                        label='Editable'
                        size='small'
                        color='default'
                        variant='outlined'
                        sx={{
                          ml: 2,
                          fontSize: '0.7rem',
                          bgcolor: 'primary.50',
                          borderColor: 'primary.200',
                          color: 'primary.600',
                        }}
                      />
                    )}
                  </Box>
                  <Grid container spacing={3}>
                    <FieldWrapper
                      label='Assigned Clinic'
                      isEditing={isEditing}
                      readValue={getClinicDisplayName(referralDocument.clinicId ?? null, clinics)}
                      gridSize={{ xs: 12, md: 6 }}>
                      <FormControl fullWidth size='small'>
                        <Select
                          value={editData.businessClinicId || ''}
                          onChange={(e) => {
                            setEditData((prev) => ({
                              ...prev,
                              businessClinicId: Number(e.target.value) || null,
                            }));
                          }}>
                          <MenuItem value=''>Not assigned</MenuItem>
                          {clinics.map((clinic: Clinic) => (
                            <MenuItem key={clinic.id} value={clinic.id}>
                              {clinic.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FieldWrapper>
                  </Grid>
                </Box>

                {/* Patient Information */}
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 3,
                      pb: 1,
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}>
                    <PersonIcon sx={{ mr: 1 }} color='primary' />
                    <Typography variant='h6'>Patient Information</Typography>
                    {isEditing && (
                      <Chip
                        label='Editable'
                        size='small'
                        color='default'
                        variant='outlined'
                        sx={{
                          ml: 2,
                          fontSize: '0.7rem',
                          bgcolor: 'primary.50',
                          borderColor: 'primary.200',
                          color: 'primary.600',
                        }}
                      />
                    )}
                  </Box>
                  <Grid container spacing={3}>
                    <FieldWrapper
                      label='First Name'
                      isEditing={isEditing}
                      readValue={referralDocument.demographics?.firstName || ''}
                      gridSize={{ xs: 12, md: 3 }}
                      required={true}>
                      <TextField
                        fullWidth
                        value={editData.firstName || ''}
                        onChange={(e) => {
                          setEditData((prev) => ({
                            ...prev,
                            firstName: e.target.value,
                          }));
                        }}
                        size='small'
                        placeholder='Enter first name'
                      />
                    </FieldWrapper>

                    <FieldWrapper
                      label='Last Name'
                      isEditing={isEditing}
                      readValue={referralDocument.demographics?.lastName || ''}
                      gridSize={{ xs: 12, md: 3 }}
                      required={true}>
                      <TextField
                        fullWidth
                        value={editData.lastName || ''}
                        onChange={(e) => {
                          setEditData((prev) => ({
                            ...prev,
                            lastName: e.target.value,
                          }));
                        }}
                        size='small'
                        placeholder='Enter last name'
                      />
                    </FieldWrapper>

                    <FieldWrapper
                      label='Date of Birth'
                      isEditing={isEditing}
                      readValue={referralDocument.demographics?.dateOfBirth}
                      gridSize={{ xs: 6, md: 3 }}
                      required={true}>
                      <TextField
                        fullWidth
                        type='date'
                        value={editData.dateOfBirth || ''}
                        onChange={(e) => {
                          setEditData((prev) => ({ ...prev, dateOfBirth: e.target.value }));
                        }}
                        size='small'
                        InputLabelProps={{ shrink: true }}
                      />
                    </FieldWrapper>

                    <FieldWrapper
                      label='Gender'
                      isEditing={isEditing}
                      readValue={formatGenderDisplay(referralDocument.demographics?.gender)}
                      gridSize={{ xs: 6, md: 3 }}
                      required={true}>
                      <FormControl fullWidth size='small'>
                        <Select
                          value={editData.gender || ''}
                          onChange={(e) => {
                            setEditData((prev) => ({ ...prev, gender: e.target.value }));
                          }}>
                          <MenuItem value=''>Select gender</MenuItem>
                          <MenuItem value={GenderType.Female}>Female</MenuItem>
                          <MenuItem value={GenderType.Male}>Male</MenuItem>
                          <MenuItem value={GenderType.Other}>Other</MenuItem>
                        </Select>
                      </FormControl>
                    </FieldWrapper>

                    <FieldWrapper
                      label='BMI'
                      isEditing={isEditing}
                      readValue={
                        referralDocument.demographics?.bmi != null
                          ? String(referralDocument.demographics.bmi)
                          : ''
                      }
                      readHint={
                        referralDocument.demographics?.bmi != null &&
                        bmiReadNote(referralDocument.bmiSource)
                      }
                      gridSize={{ xs: 6, md: 3 }}>
                      <TextField
                        fullWidth
                        type='number'
                        value={editData.bmi}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEditData((prev) => applyMeasurementEdit(prev, 'bmi', value));
                        }}
                        size='small'
                        placeholder='e.g. 27.5'
                        inputProps={{
                          step: '0.1',
                          min: String(BMI_RANGE.min),
                          max: String(BMI_RANGE.max),
                          'aria-label': 'BMI',
                        }}
                        error={Boolean(bmiField.error)}
                        helperText={bmiField.helperText}
                      />
                      {bmiField.offerCalculated && (
                        <Button
                          size='small'
                          sx={{ mt: 0.5, px: 0, textTransform: 'none' }}
                          onClick={() => setEditData((prev) => followCalculatedBmi(prev))}>
                          Use calculated BMI
                        </Button>
                      )}
                    </FieldWrapper>

                    {MEASUREMENT_FIELDS.map(({ key, label, step }) => {
                      const stored = referralDocument.demographics?.[key];
                      const error = isEditing ? measurementError(key, editData[key] ?? '') : null;
                      return (
                      <FieldWrapper
                        key={key}
                        label={label}
                        isEditing={isEditing}
                        readValue={stored != null ? String(stored) : ''}
                        gridSize={{ xs: 4, md: 2 }}>
                        <TextField
                          fullWidth
                          type='number'
                          inputProps={{ step, min: '0', 'aria-label': label }}
                          value={editData[key] ?? ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            // A derived BMI follows height and weight, and is cleared when
                            // they stop giving one; a typed BMI is left alone.
                            setEditData((prev) => applyMeasurementEdit(prev, key, value));
                          }}
                          error={Boolean(error)}
                          helperText={error ?? undefined}
                          size='small'
                        />
                      </FieldWrapper>
                      );
                    })}

                    <FieldWrapper
                      label='Street'
                      isEditing={isEditing}
                      readValue={referralDocument.demographics?.street || ''}
                      gridSize={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        value={editData.street || ''}
                        onChange={(e) => {
                          setEditData((prev) => ({
                            ...prev,
                            street: e.target.value,
                          }));
                        }}
                        size='small'
                        placeholder='Street address'
                      />
                    </FieldWrapper>

                    <FieldWrapper
                      label='City'
                      isEditing={isEditing}
                      readValue={referralDocument.demographics?.city || ''}
                      gridSize={{ xs: 12, md: 3 }}>
                      <TextField
                        fullWidth
                        value={editData.city || ''}
                        onChange={(e) => {
                          setEditData((prev) => ({ ...prev, city: e.target.value }));
                        }}
                        size='small'
                        placeholder='City'
                      />
                    </FieldWrapper>

                    <FieldWrapper
                      label='State'
                      isEditing={isEditing}
                      readValue={referralDocument.demographics?.state || ''}
                      gridSize={{ xs: 6, md: 2 }}>
                      <TextField
                        fullWidth
                        value={editData.state || ''}
                        onChange={(e) => {
                          setEditData((prev) => ({ ...prev, state: e.target.value }));
                        }}
                        size='small'
                        placeholder='State'
                      />
                    </FieldWrapper>

                    <FieldWrapper
                      label='ZIP Code'
                      isEditing={isEditing}
                      readValue={referralDocument.demographics?.zipCode || ''}
                      gridSize={{ xs: 6, md: 3 }}>
                      <TextField
                        fullWidth
                        value={editData.zipCode || ''}
                        onChange={(e) => {
                          setEditData((prev) => ({
                            ...prev,
                            zipCode: e.target.value,
                          }));
                        }}
                        size='small'
                        placeholder='ZIP Code'
                      />
                    </FieldWrapper>

                    <FieldWrapper
                      label='Email'
                      isEditing={isEditing}
                      readValue={referralDocument.demographics?.email}
                      gridSize={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        value={editData.email || ''}
                        onChange={(e) => {
                          setEditData((prev) => ({ ...prev, email: e.target.value.toLowerCase() }));
                        }}
                        size='small'
                        placeholder='Enter email'
                      />
                    </FieldWrapper>

                    <FieldWrapper
                      label='Mobile Phone'
                      isEditing={isEditing}
                      readValue={getPhoneNumberByType(
                        PhoneNumberType.Cell,
                        referralDocument.demographics?.telephone,
                      )}
                      gridSize={{ xs: 12, md: 3 }}
                      required={true}>
                      <TextField
                        fullWidth
                        value={editData.mobilePhone || ''}
                        onChange={(e) => {
                          setEditData((prev) => ({ ...prev, mobilePhone: e.target.value }));
                        }}
                        size='small'
                        placeholder='Mobile phone'
                      />
                    </FieldWrapper>

                    <FieldWrapper
                      label='Home Phone'
                      isEditing={isEditing}
                      readValue={getPhoneNumberByType(
                        PhoneNumberType.Home,
                        referralDocument.demographics?.telephone,
                      )}
                      gridSize={{ xs: 12, md: 3 }}>
                      <TextField
                        fullWidth
                        value={editData.homePhone || ''}
                        onChange={(e) => {
                          setEditData((prev) => ({ ...prev, homePhone: e.target.value }));
                        }}
                        size='small'
                        placeholder='Home phone'
                      />
                    </FieldWrapper>

                    <FieldWrapper
                      label='Other Phone'
                      isEditing={isEditing}
                      readValue={getPhoneNumberByType(
                        PhoneNumberType.Other,
                        referralDocument.demographics?.telephone,
                      )}
                      gridSize={{ xs: 12, md: 3 }}>
                      <TextField
                        fullWidth
                        value={editData.otherPhone || ''}
                        onChange={(e) => {
                          setEditData((prev) => ({ ...prev, otherPhone: e.target.value }));
                        }}
                        size='small'
                        placeholder='Other phone'
                      />
                    </FieldWrapper>
                  </Grid>
                </Box>

                {/* Referral Source Information */}
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 3,
                      pb: 1,
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}>
                    <AccountBalanceIcon sx={{ mr: 1 }} color='primary' />
                    <Typography variant='h6'>Referral Source Information</Typography>
                    {isEditing && (
                      <Chip
                        label='Editable'
                        size='small'
                        color='default'
                        variant='outlined'
                        sx={{
                          ml: 2,
                          fontSize: '0.7rem',
                          bgcolor: 'primary.50',
                          borderColor: 'primary.200',
                          color: 'primary.600',
                        }}
                      />
                    )}
                  </Box>
                  <Grid container spacing={3}>
                    {/*
                      One selector, replacing a Community Referral checkbox and a read-only
                      System Source field that both derived from the same boolean. A boolean
                      cannot carry a third cohort, so Enara Maintain was invisible here and
                      unreachable — and saving would have quietly rewritten it to
                      partner-referral.

                      `isCommunityReferral` is kept in step because other code still reads
                      it; the selector owns the value and the flag follows.
                    */}
                    <FieldWrapper
                      label='Cohort'
                      isEditing={isEditing}
                      readValue={cohortLabel(
                        referralDocument.systemSource ??
                          (referralDocument.isCommunityReferral
                            ? LeadSystemSource.CommunityReferral
                            : LeadSystemSource.PartnerReferral),
                      )}
                      gridSize={{ xs: 12, md: 4 }}>
                      <TextField
                        select
                        fullWidth
                        size='small'
                        value={editData.systemSource || LeadSystemSource.PartnerReferral}
                        onChange={(e) => {
                          const systemSource = e.target.value as LeadSystemSource;
                          setEditData((prev) => ({
                            ...prev,
                            systemSource,
                            isCommunityReferral:
                              systemSource === LeadSystemSource.CommunityReferral,
                          }));
                        }}
                        sx={{ mt: 1 }}>
                        {/*
                          Not gated by the clinic's opt-in the way the creation form is:
                          this tab's `clinics` prop is `{ id, name }` only, so the data is
                          not here, and inventing it would be worse than leaving it out.

                          Less costly than it sounds. The creation form gates because
                          `system_source` used to be write-once, so a wrong pick meant
                          recreating the lead — which is exactly what this field now
                          undoes. And as that form's own comment says, the server-side
                          check is the real control; leads-api rejecting a cohort the
                          clinic cannot serve is the follow-up that closes it properly.
                        */}
                        {PROGRAM_COHORT_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </FieldWrapper>
                    <FieldWrapper
                      label='Referring Provider'
                      isEditing={isEditing}
                      readValue={referralDocument.referralSource?.provider?.fullName || ''}
                      gridSize={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        value={editData.referringProvider || ''}
                        onChange={(e) => {
                          setEditData((prev) => ({
                            ...prev,
                            referringProvider: e.target.value,
                          }));
                        }}
                        size='small'
                        placeholder='Enter referring provider'
                      />
                    </FieldWrapper>

                    {(isEditing
                      ? editData.isCommunityReferral
                      : referralDocument.isCommunityReferral) && (
                      <FieldWrapper
                        label='Community Referral Clinic Name'
                        isEditing={isEditing}
                        readValue={referralDocument.referralSource?.sourceClinicName || ''}
                        gridSize={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          value={editData.communityReferralClinicName || ''}
                          onChange={(e) => {
                            setEditData((prev) => ({
                              ...prev,
                              communityReferralClinicName: e.target.value,
                            }));
                          }}
                          size='small'
                          placeholder='Enter community referral clinic name'
                        />
                      </FieldWrapper>
                    )}
                  </Grid>
                </Box>

                {/* Insurance Information */}
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 3,
                      pb: 1,
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    }}>
                    <LocalHospitalIcon sx={{ mr: 1 }} color='primary' />
                    <Typography variant='h6'>Insurance Information</Typography>
                    {isEditing && (
                      <Chip
                        label='Editable'
                        size='small'
                        color='default'
                        variant='outlined'
                        sx={{
                          ml: 2,
                          fontSize: '0.7rem',
                          bgcolor: 'primary.50',
                          borderColor: 'primary.200',
                          color: 'primary.600',
                        }}
                      />
                    )}
                  </Box>
                  {isEditing ? (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                        <Button
                          size='small'
                          variant='contained'
                          startIcon={<AddIcon />}
                          onClick={addInsuranceRow}
                          disabled={nextCoverageType(editData.insurance ?? []) === null}>
                          Add Insurance
                        </Button>
                      </Box>

                      {editData.insurance?.map((insurance, index) => (
                        <Box key={index} sx={{ mb: 3 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 2,
                            }}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                              {insurance.type} Insurance
                            </Typography>
                            {editData.insurance.length > 1 && (
                              <IconButton
                                size='small'
                                onClick={() => {
                                  removeInsuranceRow(index);
                                }}>
                                <DeleteIcon fontSize='small' />
                              </IconButton>
                            )}
                          </Box>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              {/*
                                Required on the primary only, which is the API's rule
                                (carrier and policy number, whenever insurance is sent).
                              */}
                              <TextField
                                fullWidth
                                required={insurance.type === 'primary'}
                                label='Insurance Carrier'
                                value={insurance.carrier}
                                onChange={(e) => {
                                  updateInsurance(index, 'carrier', e.target.value);
                                }}
                                size='small'
                                placeholder='Enter insurance carrier'
                              />
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <TextField
                                fullWidth
                                required={insurance.type === 'primary'}
                                label='Policy Number'
                                value={insurance.policyNumber}
                                onChange={(e) => {
                                  updateInsurance(index, 'policyNumber', e.target.value);
                                }}
                                size='small'
                                placeholder='Enter policy number'
                              />
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <TextField
                                fullWidth
                                label='Group Number'
                                value={insurance.groupNumber}
                                onChange={(e) => {
                                  updateInsurance(index, 'groupNumber', e.target.value);
                                }}
                                size='small'
                                placeholder='Enter group number'
                              />
                            </Grid>
                          </Grid>
                          <CoverageDetail insurance={insurance} />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box>
                      {referralDocument.insurance?.map(
                        (insurance: ReferralInsurance, index: number) => (
                          <Box key={index} sx={{ mb: 2 }}>
                            <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
                              {insurance.type} Insurance
                            </Typography>
                            <Grid container spacing={2}>
                              <FieldWrapper
                                label='Insurance Carrier'
                                isEditing={false}
                                readValue={insurance.carrier}
                                gridSize={{ xs: 12, md: 6 }}></FieldWrapper>
                              <FieldWrapper
                                label='Policy Number'
                                isEditing={false}
                                readValue={insurance.policyNumber}
                                gridSize={{ xs: 6, md: 3 }}></FieldWrapper>
                              <FieldWrapper
                                label='Group Number'
                                isEditing={false}
                                readValue={insurance.groupNumber}
                                gridSize={{ xs: 6, md: 3 }}></FieldWrapper>
                            </Grid>
                            <CoverageDetail insurance={insurance} />
                          </Box>
                        ),
                      ) || (
                        <Typography variant='body2' color='text.disabled'>
                          No insurance information available
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          );
        },
      )}

      <DiscardReferralDialog
        // Remounts per referral, so an error from one discard can never show on the next.
        key={discarding ?? 'closed'}
        referralId={discarding}
        onClose={() => setDiscarding(null)}
        onDiscarded={() => {
          const referralId = discarding;
          setDiscarding(null);
          setUpdateMessage({ type: AlertType.Success, text: 'Referral discarded.' });
          if (referralId !== null) {
            // Read-only at once, not after the refresh.
            setLocallyDiscarded((current) => ({
              ...current,
              [referralId]: new Date().toISOString(),
            }));
          }

          // Refresh after a pause, the way the save path does. Calling the parent straight
          // away swapped the tab for its loading skeleton in the same tick, so the success
          // alert was never seen. The refresh brings the referral back with `discardedAt`
          // (leads-api #125), so it renders read-only, with the date and reason.
          later(() => {
            setUpdateMessage(null);
            if (referralId && onReferralUpdate) {
              onReferralUpdate(referralId, true, 'Referral discarded.');
            }
          }, 2000);
        }}
      />
    </Box>
  );
};
